<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps<{
	duration: number
}>();

const emit = defineEmits<{
	(event: 'end'): void
	(event: 'tick', total: number): void
}>();

const showTimer = ref(true);
const minutes = ref('00');
const seconds = ref('00');

const timeInterval = ref<ReturnType<typeof setInterval> | null>(null);
const deadline = ref<Date | null>(null);

const getTimeRemaining = () => {
	if (!deadline.value) {
		return { total: 0, minutes: 0, seconds: 0 };
	}

	const total = Math.floor((deadline.value.getTime() - Date.now()) / 1000);

	return {
		total,
		minutes: Math.floor((total / 60) % 60),
		seconds: Math.floor(total % 60),
	};
};

const setZero = (num: number): string => {
	return num >= 0 && num < 10 ? `0${num}` : `${num}`;
};

const updateClock = () => {
	const time = getTimeRemaining();

	minutes.value = setZero(time.minutes);
	seconds.value = setZero(time.seconds);
	emit('tick', time.total);

	if (time.total <= 0) {
		if (timeInterval.value) clearInterval(timeInterval.value);
		showTimer.value = false;
		emit('end');
	}
};

const setClock = () => {
	timeInterval.value = setInterval(updateClock, 1000);
	updateClock();
};

const initTimer = (duration: number) => {
	if (timeInterval.value) {
		clearInterval(timeInterval.value);
		timeInterval.value = null;
	}

	deadline.value = new Date(Date.now() + duration * 1000);
	setClock();
};

onMounted(() => {
	initTimer(props.duration);
});

watch(
	() => props.duration,
	(newDuration: number) => {
		initTimer(newDuration);
	}
);

onBeforeUnmount(() => {
	if (timeInterval.value) clearInterval(timeInterval.value);
});
</script>

<template>
	<div class="core-timer">
		<span>{{ minutes }}</span>:<span>{{ seconds }}</span>
	</div>
</template>
